/**
 * i18n PreLLM — vue-i18n 11 (Composition API, legacy: false).
 *
 * - Locales supportées : FR + EN. La source historique de l'UI est française ;
 *   l'EN permet la distribution internationale (banques/assurances/public).
 * - Détection de la locale au boot : `localStorage.prellm_locale` (choix
 *   explicite de l'utilisateur) → langue navigateur (fr* → fr, sinon en) →
 *   défaut `fr`.
 * - `globalInjection: true` → `$t(...)` dispo dans tous les templates sans
 *   import. Les composants `<script setup>` utilisent `useI18n()`.
 *
 * Cf. PRELLM_SPEC.md §13 (clean archi front — i18n = infrastructure).
 */
import { createI18n } from 'vue-i18n'

import en from './locales/en.json'
import fr from './locales/fr.json'

export const SUPPORTED_LOCALES = ['fr', 'en'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

export const LOCALE_STORAGE_KEY = 'prellm_locale'
const DEFAULT_LOCALE: AppLocale = 'fr'
const FALLBACK_LOCALE: AppLocale = 'en'

function isSupported(value: string | null | undefined): value is AppLocale {
    return value === 'fr' || value === 'en'
}

/**
 * detectInitialLocale : choix persisté > langue navigateur > défaut.
 * Pur (testable) — prend le storage + navigator language en paramètres.
 */
export function detectInitialLocale(
    stored: string | null,
    navigatorLang: string | undefined,
): AppLocale {
    if (isSupported(stored)) {
        return stored
    }
    if (navigatorLang && navigatorLang.toLowerCase().startsWith('fr')) {
        return 'fr'
    }
    if (navigatorLang && navigatorLang.toLowerCase().startsWith('en')) {
        return 'en'
    }
    return DEFAULT_LOCALE
}

function readStoredLocale(): string | null {
    try {
        return localStorage.getItem(LOCALE_STORAGE_KEY)
    } catch {
        return null // localStorage indisponible (mode privé strict) → défaut.
    }
}

export const i18n = createI18n({
    legacy: false,
    globalInjection: true,
    locale: detectInitialLocale(
        readStoredLocale(),
        typeof navigator !== 'undefined' ? navigator.language : undefined,
    ),
    fallbackLocale: FALLBACK_LOCALE,
    messages: { fr, en },
})

/**
 * setLocale : change la locale active + persiste le choix + reflète sur
 * `<html lang>`. Appelé par le switcher de langue.
 */
export function setLocale(locale: AppLocale): void {
    i18n.global.locale.value = locale
    try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    } catch {
        // best-effort : si localStorage est bloqué, la locale tient pour
        // la session courante seulement.
    }
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('lang', locale)
    }
}

/** getLocale : locale active courante. */
export function getLocale(): AppLocale {
    return i18n.global.locale.value as AppLocale
}
